import { useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  ModalProps,
  Row,
  Select
} from 'antd'
import dayjs from 'dayjs'
import { useEffect } from 'react'

import {
  useClassifierServiceGetInvestigationClassifierEndpoint,
  useClassifierServiceGetInvestigationTestTypeEndpoint,
  useInvestigationsServiceGetInvestigationTestEndpointKey,
  useInvestigationsServiceGetInvestigationTestsEndpointKey,
  useInvestigationsServiceGetListSpecimensEndpointKey,
  useInvestigationsServiceUpdateInvestigationTestResultEndpoint
} from 'src/api/queries'
import { ApiError, InvestigationTestResultDto } from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import { message, SaveConfirm } from 'src/components'
import {
  dayMonthYearHourMinutesFormatWithDot,
  disabledFutureDate
} from 'src/libs'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

import { useStoredResultOptions } from '../hooks'

type Props = {
  result: InvestigationTestResultDto
  onCancel: () => void
  onSuccess?: () => void
  /** Approval state of the test the result belongs to. An approved result was
   *  already sent to the requesting doctor, which the modal warns about. */
  isApproved?: boolean
  /** The investigation type classifier id. When available, result/value
   *  options are read from it (same source AddResultModal uses to create the
   *  result), since the standalone test-type endpoint doesn't reliably
   *  hydrate nested value options. */
  typeId?: string
} & ModalProps

/** Corrects the values of an already recorded result, whatever its source
 *  (manual entry, laboratory device or external laboratory). The sample
 *  registration of an external result is edited in EditExternalResultModal
 *  instead; this modal only touches the result itself. */
export const EditTestResultModal = ({
  result,
  onCancel,
  onSuccess,
  isApproved,
  typeId,
  ...props
}: Props) => {
  const { t, currentLang } = useLocales()
  const [form] = Form.useForm()
  const queryClient = useQueryClient()

  const lang = currentLang.value

  const resultOptionId = Form.useWatch('resultOptionId', form)

  // The standalone test-type-by-id endpoint doesn't reliably hydrate nested
  // value options, so it's only used (when the caller didn't already pass
  // typeId) to read the test type's own classifierId — the id of its parent
  // investigation type — which is then read the same reliable way
  // AddResultModal does.
  const { data: testTypeMeta, isLoading: isTestTypeMetaLoading } =
    useClassifierServiceGetInvestigationTestTypeEndpoint(
      { id: String(result.testType?.id) },
      [result.testType?.id],
      { enabled: !typeId && !!result.testType?.id }
    )

  const effectiveTypeId = typeId ?? testTypeMeta?.classifierId

  const { data: investigation, isLoading: isInvestigationLoading } =
    useClassifierServiceGetInvestigationClassifierEndpoint(
      { id: String(effectiveTypeId) },
      [effectiveTypeId],
      { enabled: !!effectiveTypeId }
    )

  const isLoading = isTestTypeMetaLoading || isInvestigationLoading

  const resultOptions =
    investigation?.testTypes?.find((item) => item.id === result.testType?.id)
      ?.resultOptions ?? []
  const valueOptions =
    resultOptions.find((option) => option.id === resultOptionId)
      ?.valueOptions ?? []

  const { resultOptionItems, valueOptionItems } = useStoredResultOptions({
    result,
    lang,
    resultOptions,
    valueOptions,
    resultOptionId,
    isOptionsLoading: isLoading
  })

  useEffect(() => {
    form.setFieldsValue({
      resultOptionId: result.resultOption?.id ?? undefined,
      resultValueOptionId: result.resultValueOption?.id ?? undefined,
      resultValue: result.resultValue ?? undefined,
      resultDate: result.resultDate
        ? dayjs.utc(result.resultDate).tz()
        : dayjs()
    })
  }, [result])

  const invalidate = () =>
    [
      useInvestigationsServiceGetInvestigationTestsEndpointKey,
      useInvestigationsServiceGetInvestigationTestEndpointKey,
      useInvestigationsServiceGetListSpecimensEndpointKey
    ].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }))

  const { mutate, isPending } =
    useInvestigationsServiceUpdateInvestigationTestResultEndpoint({
      onError: (err: ApiError) => formErrorHandler(err.body?.errors),
      onSuccess: () => {
        message.success(t('tests_results.result_updated'))
        invalidate()
        onSuccess?.()
        onCancel()
      }
    })

  // A qualitative value belongs to the option it was picked under, so changing
  // the option drops the stale value.
  const onResultOptionChange = () => form.resetFields(['resultValueOptionId'])

  const onSubmit = async () => {
    const values = await form.validateFields()

    if (!result.id) {
      return message.error(t('errors.something_went_wrong'))
    }

    mutate({
      id: result.id,
      requestBody: {
        resultOptionId: values.resultOptionId || undefined,
        resultValueOptionId: values.resultValueOptionId || undefined,
        resultValue: values.resultValue ?? undefined,
        resultDate: dayjs(values.resultDate).format()
      }
    })
  }

  return (
    <Modal
      open
      centered
      width={700}
      footer={null}
      maskClosable={false}
      loading={isLoading}
      title={t('tests_results.edit_result')}
      onCancel={onCancel}
      {...props}
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <Flex vertical gap={16}>
          {isApproved && (
            <Alert
              type="warning"
              showIcon
              message={t('tests_results.edit_approved_result_warning')}
            />
          )}

          <Row gutter={[16, 12]}>
            <Col span={24}>
              <Form.Item label={t('specimens.test_type')}>
                <Input
                  readOnly
                  value={result.testType?.translation?.[lang] ?? '-'}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="resultOptionId"
                label={t('tests_results.result')}
              >
                <Select
                  allowClear
                  placeholder={t('tests_results.result')}
                  options={resultOptionItems}
                  onChange={onResultOptionChange}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="resultValueOptionId"
                label={t('specimens.result_option')}
              >
                <Select
                  allowClear
                  disabled={!resultOptionId}
                  placeholder={t('specimens.result_option')}
                  options={valueOptionItems}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="resultValue"
                label={t('investigations.result_numeric_value')}
              >
                <InputNumber
                  className="full-width"
                  placeholder={t('investigations.result_numeric_value')}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="resultDate"
                label={t('tests.result_datetime')}
                rules={requiredRules}
              >
                <DatePicker
                  className="full-width"
                  showTime
                  showSecond={false}
                  disabledDate={disabledFutureDate}
                  format={dayMonthYearHourMinutesFormatWithDot}
                />
              </Form.Item>
            </Col>
          </Row>

          <Flex justify="center" gap={8}>
            <Button onClick={onCancel}>{t('common.cancel')}</Button>

            <SaveConfirm
              title={`${t('common.confirm')}?`}
              onConfirm={onSubmit}
              okButtonProps={{ loading: isPending }}
            >
              <Button type="primary">{t('common.confirm')}</Button>
            </SaveConfirm>
          </Flex>
        </Flex>
      </Form>
    </Modal>
  )
}
